import { Component, HostListener, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { lockScroll, unlockScroll } from './shared/scroll-lock';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { VersionService, SolarData, SolarHistoryPoint, ThermometerReading, DayStat, TempDataPoint, WastePickup, UnifiSnapshot, FroelingData, FroelingHistoryPoint, PelletDayStat, ZisterneData, ZisterneHistoryPoint } from './version.service';
import { COMMIT_HASH } from '../version';
import { ColDef, GridOptions } from 'ag-grid-community';
import { todayLocalISO } from './shared/format.utils';

export type LogLevel = 'all' | 'low' | 'medium' | 'high';

@Component({
  selector: 'app-dashboard',
  templateUrl: './app.component.html'
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly title          = 'Ebersgöns Dashboard';
  readonly frontendCommit = COMMIT_HASH;
  readonly currentYear    = new Date().getFullYear();

  solar:               SolarData | null                          = null;
  thermometers:        ThermometerReading[]                      = [];
  thermometerHistory:  {[deviceId: string]: TempDataPoint[]}     = {};

  solarHistory:          SolarHistoryPoint[]    = [];
  solarChartOpen         = false;
  heizungChartOpen       = false;

  solarFlash      = false;
  thermoFlash     = false;
  liveDotOn       = true;
  solarUpdatedAt: number | null   = null;
  thermoUpdatedAt: number | null  = null;
  nowTick         = Date.now();
  private _liveTimer: any;
  private wakeLock: any = null;
  isOnline = navigator.onLine;
  private readonly onOnline  = () => { this.isOnline = true; };
  private readonly onOffline = () => { this.isOnline = false; };
  private readonly onPageShow = (e: PageTransitionEvent) => {
    if (e.persisted) {
      // bfcache restore: force a full fresh load with a cache-buster so the new bundle
      // is actually executed and bfcache cannot intercept the reload itself.
      location.replace(location.pathname + '?_t=' + Date.now() + location.hash);
    }
  };
  private readonly onVisibilityChange = async () => {
    if (document.visibilityState === 'visible') await this.requestWakeLock();
  };

  stats:               DayStat[]                                 = [];
  statsLoaded          = false;
  wasteLoaded          = false;
  froeling: FroelingData | null = null;
  froelingHistory: FroelingHistoryPoint[] = [];
  froelingFlash = false;
  froelingUpdatedAt: number | null = null;
  pelletStats: PelletDayStat[] = [];
  zisterne: ZisterneData | null = null;
  zisterneHistory: ZisterneHistoryPoint[] = [];
  zisterneFlash = false;
  zisterneUpdatedAt: number | null = null;
  unifi: UnifiSnapshot | null = null;
  unifiTimedOut = false;
  private static readonly VALID_TABS = ['energie','klima','heizung','zisterne','muell','netzwerk','statistik'] as const;
  activeTab: 'energie' | 'klima' | 'heizung' | 'zisterne' | 'muell' | 'statistik' | 'netzwerk' = (() => {
    const h = location.hash.replace('#','');
    return (AppComponent.VALID_TABS as readonly string[]).includes(h)
      ? h as any : 'energie';
  })();
  isMobileView = window.innerWidth < 768;

  setTab(tab: 'energie' | 'klima' | 'heizung' | 'zisterne' | 'muell' | 'statistik' | 'netzwerk'): void {
    this.activeTab = tab;
    location.hash = tab;
  }

  @HostListener('window:resize')
  onResize() { this.isMobileView = window.innerWidth < 768; }

  wastePickups:        WastePickup[]                             = [];
  nextPickupsByType:   { type: string; name: string; date: string; icon: string }[] = [];
  logs:                any[]                                     = [];
  logLevel: LogLevel         = 'all';
  adminOpen    = false;
  adminSection: 'logs' | 'clients' = 'logs';
  clients: { id: string; ip: string; userAgent: string; connectedAt: number; xForwardedFor: string; xRealIp: string; remoteAddr: string }[] = [];
  darkMode  = false;

  colDefs: ColDef[] = [];

  readonly gridOptions: GridOptions = {
    animateRows: true,
    rowHeight: 32,
    headerHeight: 36,
    enableCellTextSelection: true,
    ensureDomOrder: true,
    onFirstDataRendered: (e) => {
      e.api.autoSizeColumns(['ts', 'level', 'version', 'className', 'stack']);
    },
    onCellDoubleClicked: (e) => {
      const val = e.value != null ? String(e.value) : '';
      navigator.clipboard.writeText(val).then(() => {
        this.copiedCell = true;
        setTimeout(() => { this.copiedCell = false; }, 1200);
      }).catch(() => {});
    },
  };

  readonly location   = location;
  copiedCell          = false;
  expandedStackRows   = new Set<number>();
  langPickerOpen  = false;
  langSearch      = '';
  changelogOpen   = false;
  changelog: { hash: string; subject: string; date: string; author: string }[] = [];
  apiDocsOpen     = false;
  apiResources: { name: string; description: string; auth: boolean; wsType: string; url: string }[] = [];

  readonly languages = [
    { code: 'en', flag: '🇬🇧', name: 'English',    native: 'English' },
    { code: 'de', flag: '🇩🇪', name: 'German',     native: 'Deutsch' },
    { code: 'fr', flag: '🇫🇷', name: 'French',     native: 'Français' },
    { code: 'es', flag: '🇪🇸', name: 'Spanish',    native: 'Español' },
    { code: 'pt', flag: '🇵🇹', name: 'Portuguese', native: 'Português' },
    { code: 'it', flag: '🇮🇹', name: 'Italian',    native: 'Italiano' },
    { code: 'nl', flag: '🇳🇱', name: 'Dutch',      native: 'Nederlands' },
    { code: 'pl', flag: '🇵🇱', name: 'Polish',     native: 'Polski' },
    { code: 'ru', flag: '🇷🇺', name: 'Russian',    native: 'Русский' },
    { code: 'uk', flag: '🇺🇦', name: 'Ukrainian',  native: 'Українська' },
    { code: 'tr', flag: '🇹🇷', name: 'Turkish',    native: 'Türkçe' },
    { code: 'ar', flag: '🇸🇦', name: 'Arabic',     native: 'العربية' },
    { code: 'he', flag: '🇮🇱', name: 'Hebrew',     native: 'עברית' },
    { code: 'fa', flag: '🇮🇷', name: 'Persian',    native: 'فارسی' },
    { code: 'zh', flag: '🇨🇳', name: 'Chinese',    native: '中文' },
    { code: 'ja', flag: '🇯🇵', name: 'Japanese',   native: '日本語' },
    { code: 'ko', flag: '🇰🇷', name: 'Korean',     native: '한국어' },
    { code: 'hi', flag: '🇮🇳', name: 'Hindi',      native: 'हिन्दी' },
    { code: 'bn', flag: '🇧🇩', name: 'Bengali',    native: 'বাংলা' },
    { code: 'ur', flag: '🇵🇰', name: 'Urdu',       native: 'اردو' },
    { code: 'id', flag: '🇮🇩', name: 'Indonesian', native: 'Bahasa Indonesia' },
    { code: 'ms', flag: '🇲🇾', name: 'Malay',      native: 'Bahasa Melayu' },
    { code: 'th', flag: '🇹🇭', name: 'Thai',       native: 'ภาษาไทย' },
    { code: 'vi', flag: '🇻🇳', name: 'Vietnamese', native: 'Tiếng Việt' },
    { code: 'sv', flag: '🇸🇪', name: 'Swedish',    native: 'Svenska' },
    { code: 'no', flag: '🇳🇴', name: 'Norwegian',  native: 'Norsk' },
    { code: 'da', flag: '🇩🇰', name: 'Danish',     native: 'Dansk' },
    { code: 'fi', flag: '🇫🇮', name: 'Finnish',    native: 'Suomi' },
    { code: 'cs', flag: '🇨🇿', name: 'Czech',      native: 'Čeština' },
    { code: 'sk', flag: '🇸🇰', name: 'Slovak',     native: 'Slovenčina' },
    { code: 'hu', flag: '🇭🇺', name: 'Hungarian',  native: 'Magyar' },
    { code: 'ro', flag: '🇷🇴', name: 'Romanian',   native: 'Română' },
    { code: 'bg', flag: '🇧🇬', name: 'Bulgarian',  native: 'Български' },
    { code: 'hr', flag: '🇭🇷', name: 'Croatian',   native: 'Hrvatski' },
    { code: 'sr', flag: '🇷🇸', name: 'Serbian',    native: 'Српски' },
    { code: 'el', flag: '🇬🇷', name: 'Greek',      native: 'Ελληνικά' },
    { code: 'sw', flag: '🇰🇪', name: 'Swahili',    native: 'Kiswahili' },
    { code: 'am', flag: '🇪🇹', name: 'Amharic',    native: 'አማርኛ' },
    { code: 'af', flag: '🇿🇦', name: 'Afrikaans',  native: 'Afrikaans' },
    { code: 'ca', flag: '🇦🇩', name: 'Catalan',    native: 'Català' },
    { code: 'eu', flag: '🏴', name: 'Basque',      native: 'Euskara' },
    { code: 'gl', flag: '🇪🇸', name: 'Galician',   native: 'Galego' },
    { code: 'lt', flag: '🇱🇹', name: 'Lithuanian', native: 'Lietuvių' },
    { code: 'lv', flag: '🇱🇻', name: 'Latvian',    native: 'Latviešu' },
    { code: 'et', flag: '🇪🇪', name: 'Estonian',   native: 'Eesti' },
    { code: 'sl', flag: '🇸🇮', name: 'Slovenian',  native: 'Slovenščina' },
    { code: 'mk', flag: '🇲🇰', name: 'Macedonian', native: 'Македонски' },
    { code: 'sq', flag: '🇦🇱', name: 'Albanian',   native: 'Shqip' },
    { code: 'ka', flag: '🇬🇪', name: 'Georgian',   native: 'ქართული' },
    { code: 'hy', flag: '🇦🇲', name: 'Armenian',   native: 'Հայերեն' },
    { code: 'az', flag: '🇦🇿', name: 'Azerbaijani',native: 'Azərbaycan' },
    { code: 'kk', flag: '🇰🇿', name: 'Kazakh',     native: 'Қазақша' },
    { code: 'uz', flag: '🇺🇿', name: 'Uzbek',      native: 'Oʻzbekcha' },
    { code: 'mn', flag: '🇲🇳', name: 'Mongolian',  native: 'Монгол' },
    { code: 'ne', flag: '🇳🇵', name: 'Nepali',     native: 'नेपाली' },
    { code: 'si', flag: '🇱🇰', name: 'Sinhala',    native: 'සිංහල' },
    { code: 'my', flag: '🇲🇲', name: 'Burmese',    native: 'မြန်မာ' },
    { code: 'km', flag: '🇰🇭', name: 'Khmer',      native: 'ភាសាខ្មែរ' },
    { code: 'lo', flag: '🇱🇦', name: 'Lao',        native: 'ລາວ' },
    { code: 'ta', flag: '🇮🇳', name: 'Tamil',      native: 'தமிழ்' },
    { code: 'te', flag: '🇮🇳', name: 'Telugu',     native: 'తెలుగు' },
    { code: 'ml', flag: '🇮🇳', name: 'Malayalam',  native: 'മലയാളം' },
    { code: 'mr', flag: '🇮🇳', name: 'Marathi',    native: 'मराठी' },
    { code: 'gu', flag: '🇮🇳', name: 'Gujarati',   native: 'ગુજરાતી' },
    { code: 'pa', flag: '🇮🇳', name: 'Punjabi',    native: 'ਪੰਜਾਬੀ' },
    { code: 'ha', flag: '🇳🇬', name: 'Hausa',      native: 'Hausa' },
    { code: 'yo', flag: '🇳🇬', name: 'Yoruba',     native: 'Yorùbá' },
    { code: 'ig', flag: '🇳🇬', name: 'Igbo',       native: 'Igbo' },
    { code: 'zu', flag: '🇿🇦', name: 'Zulu',       native: 'IsiZulu' },
    { code: 'xh', flag: '🇿🇦', name: 'Xhosa',      native: 'IsiXhosa' },
    { code: 'so', flag: '🇸🇴', name: 'Somali',     native: 'Soomaali' },
    { code: 'ht', flag: '🇭🇹', name: 'Haitian Creole', native: 'Kreyòl Ayisyen' },
    { code: 'tl', flag: '🇵🇭', name: 'Filipino',   native: 'Filipino' },
    { code: 'jv', flag: '🇮🇩', name: 'Javanese',   native: 'Basa Jawa' },
    { code: 'cy', flag: '🏴󠁧󠁢󠁷󠁬󠁳󠁿', name: 'Welsh',       native: 'Cymraeg' },
    { code: 'ga', flag: '🇮🇪', name: 'Irish',      native: 'Gaeilge' },
    { code: 'is', flag: '🇮🇸', name: 'Icelandic',  native: 'Íslenska' },
    { code: 'lb', flag: '🇱🇺', name: 'Luxembourgish', native: 'Lëtzebuergesch' },
    { code: 'mt', flag: '🇲🇹', name: 'Maltese',    native: 'Malti' },
  ];

  readonly supportedLangs = new Set([
    'af','am','ar','az','bg','bn','ca','cs','cy','da','de','el','en','es','et',
    'eu','fa','fi','fr','ga','gl','gu','ha','he','hi','hr','ht','hu','hy','id',
    'ig','is','it','ja','jv','ka','kk','km','ko','lb','lo','lt','lv','mk','ml',
    'mn','mr','ms','mt','my','ne','nl','no','pa','pl','pt','ro','ru','si','sk',
    'sl','so','sq','sr','sv','sw','ta','te','th','tl','tr','uk','ur','uz','vi',
    'xh','yo','zh','zu'
  ]);

  get filteredLanguages() {
    const q = this.langSearch.toLowerCase();
    if (!q) return this.languages;
    return this.languages.filter(l =>
      l.name.toLowerCase().includes(q) || l.native.toLowerCase().includes(q)
    );
  }

  get currentLangFlag(): string {
    return this.languages.find(l => l.code === this.translate.currentLang)?.flag ?? '🌐';
  }

  logPassword      = '';
  logAuthError     = false;
  logAuthenticated = false;
  private adminToken = localStorage.getItem('adminSessionToken') ?? '';

  private subs: Subscription[] = [];

  constructor(
    readonly vs: VersionService,
    readonly translate: TranslateService
  ) {
    translate.addLangs(['en', 'de']);
    translate.setDefaultLang('en');

    this.darkMode = localStorage.getItem('darkMode') !== 'false';
    this.applyDarkMode();
  }

  toggleDarkMode(): void {
    this.darkMode = !this.darkMode;
    localStorage.setItem('darkMode', String(this.darkMode));
    this.applyDarkMode();
  }

  private applyDarkMode(): void {
    document.documentElement.setAttribute('data-bs-theme', this.darkMode ? 'dark' : 'light');
  }

  ngOnInit(): void {
    const saved = localStorage.getItem('lang') ?? 'en';
    this.translate.use(saved).subscribe(() => this.buildLogColDefs());

    // Rebuild log column headers when language changes
    this.subs.push(
      this.translate.onLangChange.subscribe(() => this.buildLogColDefs())
    );

    this._liveTimer = setInterval(() => { this.liveDotOn = !this.liveDotOn; }, 600);
    setInterval(() => { this.nowTick = Date.now(); }, 1000);

    this.vs.connect(this.frontendCommit);
    setTimeout(() => { if (this.unifi === null) this.unifiTimedOut = true; }, 10_000);

    this.subs.push(
      this.vs.solarHistory$.subscribe(pts => { this.solarHistory = pts; }),
      this.vs.solar$.subscribe(data => {
        this.solar = data;
        this.solarUpdatedAt = Date.now();
        const now = Date.now();
        const cutoff = now - 24 * 3600 * 1000;
        const pt: SolarHistoryPoint = { ts: now, pvYieldW: data.pvYieldW, batteryDischargeW: data.batteryDischargeW, gridConsumptionW: data.gridConsumptionW, totalConsumptionW: data.totalConsumptionW, batteryPercent: data.batteryPercent, consumptionL1W: data.consumptionL1W ?? 0, consumptionL2W: data.consumptionL2W ?? 0, consumptionL3W: data.consumptionL3W ?? 0 };
        this.solarHistory = [...this.solarHistory.filter(p => p.ts >= cutoff), pt];
        this.solarFlash = false;
        setTimeout(() => { this.solarFlash = true; setTimeout(() => { this.solarFlash = false; }, 900); }, 0);
      }),
      this.vs.thermometers$.subscribe(data => {
        this.thermometers = data;
        this.thermoUpdatedAt = Date.now();
        this.thermoFlash = false;
        setTimeout(() => { this.thermoFlash = true; setTimeout(() => { this.thermoFlash = false; }, 900); }, 0);
      }),
      this.vs.thermometerHistory$.subscribe(h => { this.thermometerHistory = h; }),
      this.vs.stats$.subscribe(data => { this.stats = data; this.statsLoaded = true; }),
      this.vs.waste$.subscribe(data => {
        this.wastePickups = data;
        this.nextPickupsByType = this._computeNextPickups(data);
        this.wasteLoaded = true;
      }),
      this.vs.unifi$.subscribe(data => { this.unifi = data; this.unifiTimedOut = false; }),
      this.vs.zisterneHistory$.subscribe(pts => { this.zisterneHistory = pts; }),
      this.vs.zisterne$.subscribe(data => {
        this.zisterne = data;
        this.zisterneUpdatedAt = Date.now();
        const now = Date.now();
        const cutoff = now - 7 * 24 * 3600 * 1000;
        const pt: ZisterneHistoryPoint = { ts: now, levelPct: data.levelPct, depthCm: data.depthCm };
        this.zisterneHistory = [...this.zisterneHistory.filter(p => p.ts >= cutoff), pt];
        this.zisterneFlash = false;
        setTimeout(() => { this.zisterneFlash = true; setTimeout(() => { this.zisterneFlash = false; }, 900); }, 0);
      }),
      this.vs.froelingHistory$.subscribe(pts => { this.froelingHistory = pts; }),
      this.vs.froeling$.subscribe(data => {
        this.froeling = data;
        this.froelingUpdatedAt = Date.now();
        const now = Date.now();
        const cutoff = now - 24 * 3600 * 1000;
        const pt: FroelingHistoryPoint = {
          ts: now,
          boilerTempC: data.boilerTempC,
          exhaustTempC: data.exhaustTempC,
          boilerModulationPct: data.boilerModulationPct,
          bufferTopTempC: data.bufferTopTempC,
          bufferBottomTempC: data.bufferBottomTempC,
          bufferChargePct: data.bufferChargePct,
          hotWaterTempC: data.hotWaterTempC,
          flowTempActualC: data.flowTempActualC,
          outdoorTempC: data.outdoorTempC,
          solarThermalTempC: data.solarThermalTempC,
          pelletConsumptionKg: data.pelletConsumptionKg,
          circulationPumpPct: data.circulationPumpPct,
          heatingCircuits: data.heatingCircuits.map(hc => ({ name: hc.name, flowTempActualC: hc.flowTempActualC, pumpState: hc.pumpState })),
        };
        this.froelingHistory = [...this.froelingHistory.filter(p => p.ts >= cutoff), pt];
        this.froelingFlash = false;
        setTimeout(() => { this.froelingFlash = true; setTimeout(() => { this.froelingFlash = false; }, 900); }, 0);
      }),
      this.vs.pelletStats$.subscribe(days => { this.pelletStats = days; }),
    );
    this.requestWakeLock();
    window.addEventListener('online',   this.onOnline);
    window.addEventListener('offline',  this.onOffline);
    window.addEventListener('pageshow', this.onPageShow);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  ngAfterViewInit(): void {
    // If the page was hidden during a document.write() swap to prevent the
    // empty-app-root flash, reveal it now that Angular has fully rendered.
    document.documentElement.style.removeProperty('visibility');
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    clearInterval(this._liveTimer);
    window.removeEventListener('online',   this.onOnline);
    window.removeEventListener('offline',  this.onOffline);
    window.removeEventListener('pageshow', this.onPageShow);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.wakeLock?.release?.();
  }

  private async requestWakeLock(): Promise<void> {
    if ('wakeLock' in navigator) {
      try { this.wakeLock = await (navigator as any).wakeLock.request('screen'); }
      catch (_) {}
    }
  }

  openChangelog(): void {
    this.changelogOpen = true;
    lockScroll();
    if (this.changelog.length === 0) {
      fetch('/api/changelog')
        .then(r => r.json())
        .then(data => { this.changelog = data; })
        .catch(() => {});
    }
  }

  closeChangelog(): void {
    if (this.changelogOpen) { this.changelogOpen = false; unlockScroll(); }
  }

  openApiDocs(): void {
    this.apiDocsOpen = true;
    lockScroll();
    if (this.apiResources.length === 0) {
      fetch('/api/data')
        .then(r => r.json())
        .then(data => { this.apiResources = data; })
        .catch(() => {});
    }
  }

  closeApiDocs(): void {
    if (this.apiDocsOpen) { this.apiDocsOpen = false; unlockScroll(); }
  }

  openLangPicker(): void {
    this.langPickerOpen = true;
    this.langSearch = '';
    lockScroll();
  }

  closeLangPicker(): void {
    if (this.langPickerOpen) { this.langPickerOpen = false; unlockScroll(); }
  }

  // ── Expanded-chart time range (Tag/Woche/Monat/Jahr) ──────────────────────
  // 'day' uses the live 24h history pushed over WS; longer ranges fetch the
  // downsampled long-term archive from /api/history.
  chartRange: 'day' | 'week' | 'month' | 'year' = 'day';
  rangeLoading = false;
  solarRangeHistory:    SolarHistoryPoint[]    = [];
  froelingRangeHistory: FroelingHistoryPoint[] = [];
  readonly chartRanges: { key: 'day' | 'week' | 'month' | 'year'; label: string }[] = [
    { key: 'day',   label: 'Tag' },
    { key: 'week',  label: 'Woche' },
    { key: 'month', label: 'Monat' },
    { key: 'year',  label: 'Jahr' },
  ];
  private static readonly RANGE_MS: Record<string, number> = {
    day:   24 * 3600 * 1000,
    week:  7 * 24 * 3600 * 1000,
    month: 30 * 24 * 3600 * 1000,
    year:  365 * 24 * 3600 * 1000,
  };

  get solarChartHistory(): SolarHistoryPoint[] {
    return this.chartRange === 'day' ? this.solarHistory : this.solarRangeHistory;
  }
  get froelingChartHistory(): FroelingHistoryPoint[] {
    return this.chartRange === 'day' ? this.froelingHistory : this.froelingRangeHistory;
  }

  get rangeWindowMs(): number {
    return AppComponent.RANGE_MS[this.chartRange];
  }

  setChartRange(range: 'day' | 'week' | 'month' | 'year', series: 'solar' | 'froeling'): void {
    this.chartRange = range;
    if (range === 'day') return;   // live data already in memory
    const to   = Date.now();
    const from = to - AppComponent.RANGE_MS[range];
    this.rangeLoading = true;
    fetch(`/api/history?series=${series}&from=${from}&to=${to}&buckets=600`)
      .then(r => r.json())
      .then((pts: any[]) => {
        if (series === 'solar') this.solarRangeHistory = pts ?? [];
        else                    this.froelingRangeHistory = pts ?? [];
        this.rangeLoading = false;
      })
      .catch(() => { this.rangeLoading = false; });
  }

  openSolarChart(): void {
    this.chartRange = 'day';
    this.solarChartOpen = true;
    lockScroll();
  }

  closeSolarChart(): void {
    if (this.solarChartOpen) { this.solarChartOpen = false; unlockScroll(); }
  }

  openHeizungChart(): void {
    this.chartRange = 'day';
    this.heizungChartOpen = true;
    lockScroll();
  }

  closeHeizungChart(): void {
    if (this.heizungChartOpen) { this.heizungChartOpen = false; unlockScroll(); }
  }

  selectLang(code: string): void {
    if (!this.supportedLangs.has(code)) return;
    this.closeLangPicker();
    this.switchLang(code);
  }

  switchLang(lang: string): void {
    localStorage.setItem('lang', lang);
    this.translate.use(lang);
  }

  private buildLogColDefs(): void {
    const t = (key: string) => this.translate.instant(key);

    this.colDefs = [
      {
        field: 'ts', headerName: t('COLS.ZEIT'), sortable: true,
        valueFormatter: p => this.fmtTs(p.value),
        sort: 'desc',
      },
      {
        field: 'level', headerName: t('COLS.LEVEL'), sortable: true, filter: true,
        cellRenderer: (p: any) => {
          const colour = this.levelBadge(p.value);
          return `<span class="badge bg-${colour}">${p.value ?? ''}</span>`;
        },
      },
      {
        field: 'version', headerName: t('COLS.VERSION'),
        valueFormatter: p => (p.value ?? '?').slice(0, 7),
      },
      { field: 'className', headerName: t('COLS.KLASSE'), sortable: true, filter: true },
      {
        field: 'message', headerName: t('COLS.NACHRICHT'), flex: 1, filter: true,
        wrapText: true, autoHeight: true,
      },
      {
        field: 'stack', headerName: t('COLS.STACK'), filter: true,
        autoHeight: true,
        cellRenderer: (p: any) => {
          const stack: string = p.value ?? '';
          if (!stack) return '<span class="text-muted">–</span>';
          const idx = p.rowIndex as number;
          const expanded = this.expandedStackRows.has(idx);
          const firstLine = stack.split('\n')[0];
          if (expanded) {
            return `<pre style="font-size:11px;line-height:1.4;margin:0;cursor:pointer;white-space:pre-wrap;word-break:break-all;" title="${this.translate.instant('STACK_EINKLAPPEN')}">▲ ${stack}</pre>`;
          }
          return `<span style="font-family:monospace;font-size:11px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;display:block;cursor:pointer;" title="${stack.replace(/"/g, '&quot;')}">▶ ${firstLine}</span>`;
        },
        onCellClicked: (p: any) => {
          const idx = p.rowIndex as number;
          if (this.expandedStackRows.has(idx)) {
            this.expandedStackRows.delete(idx);
          } else {
            this.expandedStackRows.add(idx);
          }
          p.api.redrawRows({ rowNodes: [p.node] });
        },
      },
    ];
  }

  private adminHeaders(): Record<string, string> {
    return this.adminToken ? { 'X-Session-Token': this.adminToken }
                           : { 'X-Log-Password':  this.logPassword };
  }

  private handleAuthError(): void {
    this.logAuthError     = true;
    this.logAuthenticated = false;
    // Token is invalid — clear it so the password prompt shows
    if (this.adminToken) {
      this.adminToken = '';
      localStorage.removeItem('adminSessionToken');
    }
  }

  fetchLogs(): void {
    const param = this.logLevel === 'all' ? '' : `?level=${this.logLevel}`;
    fetch(`/api/logs${param}`, { headers: this.adminHeaders() })
      .then(r => {
        if (r.status === 401) { this.handleAuthError(); this.logs = []; return null; }
        this.logAuthError     = false;
        this.logAuthenticated = true;
        return r.json();
      })
      .then(data => { if (data) this.logs = (data as any[]).reverse(); })
      .catch(() => {});
  }

  setLogLevel(level: LogLevel): void {
    this.logLevel = level;
    this.fetchLogs();
  }

  fetchClients(): void {
    fetch('/api/clients', { headers: this.adminHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(data => { this.clients = (data as any[]).sort((a, b) => a.connectedAt - b.connectedAt); })
      .catch(() => {});
  }

  toggleAdmin(): void {
    this.adminOpen = !this.adminOpen;
    if (this.adminOpen) {
      lockScroll();
      // Auto-login if a stored token exists
      if (!this.logAuthenticated && this.adminToken) {
        fetch('/api/admin/check', { headers: { 'X-Session-Token': this.adminToken } })
          .then(r => {
            if (r.ok) {
              this.logAuthenticated = true;
              if (this.adminSection === 'logs')    this.fetchLogs();
              if (this.adminSection === 'clients') this.fetchClients();
            } else {
              this.adminToken = '';
              localStorage.removeItem('adminSessionToken');
            }
          }).catch(() => {});
      } else if (this.logAuthenticated) {
        if (this.adminSection === 'logs')    this.fetchLogs();
        if (this.adminSection === 'clients') this.fetchClients();
      }
    } else {
      unlockScroll();
      this.expandedStackRows.clear();
    }
  }

  closeAdmin(): void {
    if (this.adminOpen) { this.adminOpen = false; unlockScroll(); this.expandedStackRows.clear(); }
  }

  setAdminSection(section: 'logs' | 'clients'): void {
    this.adminSection = section;
    if (section === 'logs'    && this.logAuthenticated) this.fetchLogs();
    if (section === 'clients' && this.logAuthenticated) this.fetchClients();
  }

  submitLogPassword(): void {
    fetch('/api/admin/login', { method: 'POST', headers: { 'X-Log-Password': this.logPassword } })
      .then(r => {
        if (r.status === 401) { this.handleAuthError(); return null; }
        this.logAuthError     = false;
        this.logAuthenticated = true;
        return r.json();
      })
      .then(data => {
        if (data?.token) {
          this.adminToken = data.token;
          localStorage.setItem('adminSessionToken', data.token);
        }
        this.fetchLogs();
        this.fetchClients();
      })
      .catch(() => {});
  }

  adminLogout(): void {
    fetch('/api/admin/logout', { method: 'POST', headers: { 'X-Session-Token': this.adminToken } })
      .catch(() => {});
    this.adminToken       = '';
    this.logAuthenticated = false;
    this.logPassword      = '';
    this.logAuthError     = false;
    this.logs             = [];
    this.clients          = [];
    localStorage.removeItem('adminSessionToken');
  }

  fmtTs(ts: number | string): string {
    const d = new Date(Number(ts));
    const locale = this.translate.currentLang === 'de' ? 'de-DE' : 'en-GB';
    return d.toLocaleString(locale, { dateStyle: 'short', timeStyle: 'medium' });
  }

  levelBadge(level: string): string {
    return level === 'high' ? 'danger' : level === 'medium' ? 'warning' : 'secondary';
  }

  shortHash(hash: string): string {
    if (!hash || hash.length <= 9) return hash || '…';
    return hash.slice(0, 4) + '…' + hash.slice(-4);
  }

  get todayStat(): DayStat | null {
    const today = todayLocalISO();
    return this.stats.find(s => s.date === today) ?? null;
  }

  private static readonly WASTE_ICONS: Record<string, string> = {
    RM:  'assets/waste/rm.svg',
    Bio: 'assets/waste/bio.svg',
    GT:  'assets/waste/gt.svg',
    PPK: 'assets/waste/ppk.svg',
    A:   'assets/waste/astwerk.svg',
    SoH: 'assets/waste/soh.svg',
    TB:  'assets/waste/tb.svg',
  };

  private _computeNextPickups(pickups: WastePickup[]): { type: string; name: string; date: string; icon: string }[] {
    const today = todayLocalISO();
    const map   = new Map<string, { type: string; name: string; date: string; icon: string }>();
    for (const p of pickups) {
      if (p.type === 'SoG') continue;
      if (p.date < today)  continue;
      if (!map.has(p.type)) {
        map.set(p.type, {
          type: p.type,
          name: p.name,
          date: p.date,
          icon: AppComponent.WASTE_ICONS[p.type] ?? 'assets/waste/rm.svg',
        });
      }
    }
    return [...map.values()].sort((a, b) => a.date.localeCompare(b.date));
  }
}
